import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { ChevronLeft, ChevronRight, MapPin } from "lucide-react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { useLanguage } from "@/hooks/useLanguage";

interface Project {
  id: string;
  title: string;
  title_en: string;
  slug: string;
  location: string;
  location_en: string;
  short_description: string;
  short_description_en: string;
  thumbnail_url: string | null;
}

const ProjectsSection = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const { t, lang } = useLanguage();

  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, align: "start", slidesToScroll: 1, containScroll: "trimSnaps" },
    [Autoplay({ delay: 5000, stopOnInteraction: false, stopOnMouseEnter: true })]
  );

  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi, onSelect]);

  useEffect(() => {
    const fetchProjects = async () => {
      const { data } = await supabase
        .from("projects")
        .select("id, title, title_en, slug, location, location_en, short_description, short_description_en, thumbnail_url")
        .order("sort_order", { ascending: true })
        .order("created_at", { ascending: false });
      setProjects((data as Project[]) || []);
    };
    fetchProjects();
  }, []);

  const { ref: sectionRef, isVisible } = useScrollAnimation(0.1);

  const pTitle = (p: Project) => (lang === "en" && p.title_en) ? p.title_en : p.title;
  const pLocation = (p: Project) => (lang === "en" && p.location_en) ? p.location_en : p.location;
  const pDesc = (p: Project) => (lang === "en" && p.short_description_en) ? p.short_description_en : p.short_description;

  if (projects.length === 0) return null;

  return (
    <section id="projekti" className="py-16 sm:py-20 bg-surface-warm">
      <div className="container mx-auto px-4" ref={sectionRef}>
        <div className="mb-10 sm:mb-14">
          <h2 className={`text-2xl sm:text-3xl md:text-4xl font-extrabold text-center text-heading transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            {t("projects.title")}
          </h2>
          <p className={`text-center text-muted-foreground mt-3 max-w-2xl mx-auto text-sm sm:text-base transition-all duration-700 delay-100 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            {t("projects.desc")}
          </p>
        </div>

        <div className="relative px-0 sm:px-12 md:px-14">
          {projects.length > 1 && (
            <>
              <button onClick={() => emblaApi?.scrollPrev()} disabled={!canScrollPrev} className="hidden sm:flex absolute left-0 top-1/2 -translate-y-1/2 z-10 w-11 h-11 bg-card border border-border rounded-full shadow-md items-center justify-center hover:bg-muted transition-colors disabled:opacity-40" aria-label={t("projects.prev")}>
                <ChevronLeft className="w-5 h-5 text-foreground" />
              </button>
              <button onClick={() => emblaApi?.scrollNext()} disabled={!canScrollNext} className="hidden sm:flex absolute right-0 top-1/2 -translate-y-1/2 z-10 w-11 h-11 bg-card border border-border rounded-full shadow-md items-center justify-center hover:bg-muted transition-colors disabled:opacity-40" aria-label={t("projects.next")}>
                <ChevronRight className="w-5 h-5 text-foreground" />
              </button>
            </>
          )}

          <div className="overflow-hidden rounded-xl" ref={emblaRef}>
            <div className="flex -ml-4">
              {projects.map((p) => (
                <div key={p.id} className="min-w-0 shrink-0 grow-0 basis-[85%] sm:basis-1/2 md:basis-1/3 pl-4">
                  <Link to={`/projekti/${p.slug}`} className="block bg-card rounded-xl overflow-hidden border border-border group cursor-pointer hover:shadow-xl transition-all duration-300">
                    <div className="relative overflow-hidden" style={{ aspectRatio: "3/2" }}>
                      {p.thumbnail_url ? (
                        <img src={p.thumbnail_url} alt={pTitle(p)} loading="lazy" width={480} height={320} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      ) : (
                        <div className="w-full h-full bg-muted flex items-center justify-center">
                          <span className="text-muted-foreground text-sm">{t("projects.noImage")}</span>
                        </div>
                      )}
                      <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/15 transition-colors duration-300" />
                    </div>
                    <div className="p-4 sm:p-5">
                      <h3 className="font-semibold text-base sm:text-lg mb-1 line-clamp-1">{pTitle(p)}</h3>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mb-2">
                        <MapPin className="w-3 h-3" />
                        {pLocation(p)}
                      </div>
                      <p className="text-sm text-muted-foreground line-clamp-2">{pDesc(p)}</p>
                      <span className="inline-block mt-3 text-xs font-medium text-primary hover:underline">{t("projects.view")}</span>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          </div>

          {projects.length > 1 && (
            <div className="flex justify-center gap-2 mt-6">
              {projects.map((_, i) => (
                <button key={i} onClick={() => emblaApi?.scrollTo(i)} className={`min-w-[12px] h-3 rounded-full transition-all duration-300 ${i === selectedIndex ? "bg-primary w-6" : "bg-border hover:bg-muted-foreground"}`} aria-label={`${t("projects.view")} ${i + 1}`} />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ProjectsSection;
